import { useState } from "react";
import { motion } from "framer-motion";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Prompt } from "@/lib/types";
import { PinIcon, MessageSquare, GitBranch, ArrowUp } from "lucide-react";
import { cn } from "@/lib/utils";
import { EditPromptDialog } from "@/components/Home/EditPromptDialog";
import { useAuth } from "@/context/AuthContext";
import { DeletePromptDialog } from "@/components/Home/DeletePromptDialog";
import { ReportPromptDialog } from "@/components/Home/ReportPromptDialog";
import { PromptComments } from "@/components/Home/PromptCard/PromptComments";
import { usePromptActions } from "@/hooks/prompts/use-prompt-actions";
import { supabase } from "@/integrations/supabase/client";
import { toDatabaseValue } from "@/utils/supabase/type-guards";
import { formatDistanceToNow } from "date-fns";
import { PromptActionsDropdown } from "@/components/Home/PromptCardActions/PromptActionsDropdown";
import { RemixBadge } from "@/components/Home/RemixBadge";
import { GeoQualityBadge } from "@/components/GEO/GeoQualityBadge";
import { calculateGeoQualityScore } from "@/lib/geo-quality-score";

type ProfilePromptCardProps = {
  prompt: Prompt;
  onUpdate?: (id: string, updatedPrompt: Partial<Prompt>) => void;
  onDelete?: (id: string) => void;
  onUnsave?: () => void;
};

export function ProfilePromptCard({ 
  prompt, 
  onUpdate, 
  onDelete,
  onUnsave,
}: ProfilePromptCardProps) {
  const { user } = useAuth();
  const [showComments, setShowComments] = useState(false);
  const [commentCount, setCommentCount] = useState(prompt.comments);
  const [showFullDescription, setShowFullDescription] = useState(false);
  
  const {
    isLiked,
    isSaved,
    likeCount,
    isEditDialogOpen,
    isDeleteDialogOpen,
    isReportDialogOpen,
    isDeleting,
    isReporting,
    setIsEditDialogOpen,
    setIsDeleteDialogOpen,
    setIsReportDialogOpen,
    handleLike,
    handleSave,
    handleEditSave,
    handleDelete,
    handleReport
  } = usePromptActions(prompt, user, onUpdate, onDelete);

  const qualityScore = calculateGeoQualityScore(prompt);

  const toggleComments = () => {
    setShowComments(!showComments);
    if (!showComments) {
      fetchCommentCount();
    }
  };

  const fetchCommentCount = async () => {
    try {
      const { count, error } = await supabase
        .from('prompt_comments')
        .select('*', { count: "exact", head: true })
        .eq('prompt_id', toDatabaseValue(prompt.id));
      
      if (!error && count !== null) {
        setCommentCount(count);
      }
    } catch (error) {
      console.error('Error fetching comment count:', error);
    }
  };

  const handleNewComment = () => {
    setCommentCount(prevCount => prevCount + 1);
  };

  return (
    <>
      <Card className={cn(
        "futuristic-card w-full transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl group animate-fade-in", 
        prompt.isPinned && "border-primary/50 bg-gradient-to-br from-primary/5 to-secondary/5"
      )}>
        {prompt.isPinned && (
          <div className="bg-gradient-to-r from-primary/20 to-secondary/20 px-4 py-2 flex items-center gap-2 text-xs text-primary border-b border-primary/20">
            <PinIcon className="h-3 w-3" />
            <span className="font-semibold">Pinned Prompt</span>
          </div>
        )}

        {/* Remix indicator */}
        {prompt.isRemix && prompt.remixOf && (
          <div className="bg-gradient-to-r from-accent/20 to-secondary/20 px-4 py-2 flex items-center gap-2 text-xs text-accent border-b border-accent/20">
            <GitBranch className="h-3 w-3" />
            <span className="font-semibold">Remixed from @{prompt.originalUsername}</span>
          </div>
        )}
        
        <CardHeader className="p-4 pb-2">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="secondary" className="text-xs px-2 py-1 bg-primary/10 text-primary border-primary/20">
                {prompt.category}
              </Badge>
              {/* Prominent GEO Quality Badge */}
              <GeoQualityBadge qualityScore={qualityScore} compact={false} showBreakdown={true} />
              {prompt.isRemix && (
                <RemixBadge remixOf={prompt.remixOf} originalUsername={prompt.originalUsername} />
              )}
              <span className="text-xs text-muted-foreground">
                {formatDistanceToNow(new Date(prompt.createdAt), { addSuffix: true })}
                {prompt.editedAt && (
                  <span className="ml-2">(edited)</span>
                )}
              </span>
            </div>
            <PromptActionsDropdown
              prompt={prompt}
              onEdit={() => setIsEditDialogOpen(true)}
              onDelete={() => setIsDeleteDialogOpen(true)}
              onSave={() => handleSave(!isSaved)}
              onReport={() => setIsReportDialogOpen(true)}
              isSaved={isSaved}
            />
          </div>
        </CardHeader>
        
        <CardContent className="p-4 pt-2">
          <div className="space-y-3">
            {prompt.title && (
              <h3 className="font-headline text-lg font-bold text-foreground leading-tight">
                {prompt.title}
              </h3>
            )}
            {prompt.description && (
              <div className="text-sm text-muted-foreground">
                {showFullDescription || prompt.description.length <= 120
                  ? prompt.description
                  : `${prompt.description.substring(0, 120)}...`}
                {prompt.description.length > 120 && (
                  <button
                    onClick={() => setShowFullDescription(!showFullDescription)}
                    className="ml-1 text-primary hover:underline text-xs"
                  >
                    {showFullDescription ? "see less" : "see more"}
                  </button>
                )}
              </div>
            )}
            <div className="font-body text-foreground/90 leading-relaxed whitespace-pre-wrap break-words">
              {prompt.content.length > 200 
                ? `${prompt.content.substring(0, 200)}...` 
                : prompt.content
              }
            </div>
          </div>
        </CardContent>
        
        <CardFooter className="p-4 pt-0 flex flex-wrap items-center justify-between gap-3 border-t border-border/50">
          <div className="flex items-center gap-4">
            {/* Upvote Button */}
            <motion.button
              whileTap={{ scale: 0.9 }}
              animate={{
                scale: isLiked ? 1.1 : 1,
                color: isLiked ? "#38bdf8" : "#9ca3af",
              }}
              transition={{ type: "spring", stiffness: 300, damping: 15 }}
              onClick={() => handleLike(!isLiked)}
              className="flex items-center gap-2 text-sm font-medium px-3 py-2 rounded-md hover:bg-accent/50 transition-colors"
            >
              <ArrowUp
                className={`h-4 w-4 transition-all ${
                  isLiked ? "text-sky-400 drop-shadow-md" : "text-muted-foreground"
                }`}
              />
              <span className="font-medium">{likeCount}</span>
            </motion.button>

            {/* Comment Button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleComments}
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
            >
              <MessageSquare className="h-4 w-4" />
              <span className="font-medium">{commentCount}</span>
            </Button>

            {/* Remix Button */}
            <Button
              variant="ghost"
              size="sm"
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-secondary transition-colors"
            >
              <GitBranch className="h-4 w-4" />
              <span className="font-medium">Remix</span>
            </Button>
          </div>

          {/* Save Button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleSave(!isSaved)}
            className={cn(
              "flex items-center gap-2 text-sm transition-colors",
              isSaved 
                ? "text-primary hover:text-primary/80" 
                : "text-muted-foreground hover:text-primary"
            )}
          >
            <span className={isSaved ? "animate-bounce" : ""}>🔖</span>
            <span className="font-medium">{isSaved ? "Saved" : "Save"}</span>
          </Button>
        </CardFooter>

        {showComments && (
          <div className="px-4 pb-4 border-t border-border/50 bg-muted/20">
            <PromptComments 
              promptId={prompt.id}
              onNewComment={handleNewComment}
            />
          </div>
        )}
      </Card>
      
      <EditPromptDialog
        prompt={prompt}
        open={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        onSave={handleEditSave}
      />
      
      <DeletePromptDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        onDelete={handleDelete}
      />

      <ReportPromptDialog
        open={isReportDialogOpen}
        onOpenChange={setIsReportDialogOpen}
        onReport={handleReport}
        isReporting={isReporting}
      />
    </>
  );
}
